import React, { useState, useMemo } from 'react';
import { Link, Chip, Checkbox, InputBase } from '@mui/material';
import {
  Home as HomeIcon, Business as BusinessIcon,
  Search as SearchIcon, FileDownload as ExportIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import BookingAction from './BookingAction';
import { useNavigate } from 'react-router-dom';
import { getInitials, formatDateShort, daysSince } from './bms_helper';
import {
  SmartTableTH,
  SmartTableTD,
  TableLoadingState,
  TableEmptyState,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_ROW_CLASS,
} from '../../components/common/SmartTable';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const T = {
  white:   '#ffffff',
  bg:      '#f5f8f6',
  sidebar: '#ffffff',
  ink:     '#0f1a13',
  ink2:    '#253b2c',
  subtle:  '#526b5c',
  muted:   '#8eaa98',
  border:  '#dce8e1',
  borderL: '#eaf1ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e5f5ec',
  greenSf: 'rgba(10,107,62,0.08)',

  avail:   { dot: '#22c55e', text: '#15803d', bg: '#dcfce7' },
  booked:  { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  hold:    { dot: '#a78bfa', text: '#5b21b6', bg: '#ede9fe' },
  draft:   { dot: '#94a3b8', text: '#475569', bg: '#f1f5f9' },
  confirmed:{ dot: '#22c55e', text: '#14532d', bg: '#dcfce7' },
  cancelled:{ dot: '#ef4444', text: '#991b1b', bg: '#fee2e2' },
  pending: { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  active:  { dot: '#3b82f6', text: '#1e3a8a', bg: '#dbeafe' },
  completed:{ dot: '#10b981', text: '#065f46', bg: '#d1fae5' },
  expired: { dot: '#9ca3af', text: '#374151', bg: '#f3f4f6' },
};

const STATUS_CFG = (s) => T[(s||'draft').toLowerCase()] || T.draft;

const ALL_STATUSES = ['draft','confirmed','active','booked','hold','cancelled','pending','completed'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = formatDateShort;

// ─── Sidebar item ─────────────────────────────────────────────────────────────
function SidebarProject({ project, count, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'8px 14px', cursor:'pointer', borderRadius:8, margin:'1px 6px',
        background: active ? T.greenSf : 'transparent',
        borderLeft: active ? `3px solid ${T.green}` : '3px solid transparent',
        transition:'all 120ms',
      }}
    >
      <span style={{ fontSize:13, fontWeight: active ? 700 : 500, color: active ? T.green : T.ink2, lineHeight:1.3 }}>
        {project}
      </span>
      <span style={{
        fontSize:11, fontWeight:700,
        color: active ? T.green : T.muted,
        background: active ? T.greenLt : T.bg,
        borderRadius:99, padding:'1px 7px', minWidth:22, textAlign:'center',
      }}>{count}</span>
    </div>
  );
}

function SidebarStatusItem({ status, label, count, active, onClick }) {
  const cfg = STATUS_CFG(status);
  return (
    <div
      onClick={onClick}
      style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'7px 14px', cursor:'pointer', borderRadius:8, margin:'1px 6px',
        background: active ? T.greenSf : 'transparent',
        borderLeft: active ? `3px solid ${T.green}` : '3px solid transparent',
        transition:'all 120ms',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
        <div style={{ width:7, height:7, borderRadius:'50%', background:cfg.dot }} />
        <span style={{ fontSize:12.5, fontWeight: active ? 700 : 500, color: active ? T.green : T.ink2 }}>
          {label}
        </span>
      </div>
      <span style={{
        fontSize:11, fontWeight:700,
        color: active ? T.green : T.muted,
        background: active ? T.greenLt : T.bg,
        borderRadius:99, padding:'1px 7px',
      }}>{count}</span>
    </div>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────────────
function StatusPill({ status, label }) {
  const cfg = STATUS_CFG(status);
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      background:cfg.bg, color:cfg.text,
      fontSize:11, fontWeight:700, padding:'3px 9px 3px 7px',
      borderRadius:99, whiteSpace:'nowrap',
    }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:cfg.dot }} />
      {label || status}
    </span>
  );
}

// ─── Agent avatar ─────────────────────────────────────────────────────────────
function AgentAvatar({ name }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div style={{
        width:24, height:24, borderRadius:'50%', flexShrink:0,
        background:'linear-gradient(135deg,#0a6b3e,#12a85e)',
        color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:9, fontWeight:900,
      }}>{getInitials(name)}</div>
      <span style={{ fontSize:12.5, color:T.ink2, fontWeight:500 }}>{name || '—'}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const BookingsListView = ({
  bookings: bookingsProp,
  selectedBooking,
  onSelectBooking,
  page = 1,
  rowsPerPage = 50,
  totalCount = 0,
  totalPages = 1,
  onPageChange,
  onRowsPerPageChange,
  hideTablePagination = false,
  onStatusUpdate,
  onCreateBooking,
  isLoading = false,
  statusCounts: statusCountsFromApi = {},
  activeStatusFilter = 'all',
  onStatusFilterChange,
  activeProjectFilter = null,
  onProjectFilterChange,
  sidebarProjectCounts: sidebarProjectCountsProp,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const bookings = Array.isArray(bookingsProp) ? bookingsProp : [];

  const [search, setSearch] = useState('');
  const [internalPage, setInternalPage] = useState(1);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(15);

  const serverSide = typeof onPageChange === 'function' && typeof totalCount === 'number';

  // Build sidebar project counts from current bookings when no sidebar override
  const projectCountsFromBookings = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      const n = b.project?.name || 'No Project';
      const id = b.project?.id ?? null;
      if (!map[n]) map[n] = { count: 0, projectId: id };
      map[n].count += 1;
    });
    return map;
  }, [bookings]);

  // Use passed full project counts when available (keeps all projects visible when one is selected)
  const projectCounts = sidebarProjectCountsProp && Object.keys(sidebarProjectCountsProp).length > 0
    ? sidebarProjectCountsProp
    : projectCountsFromBookings;

  const allBookingsCount = useMemo(() => {
    if (sidebarProjectCountsProp && Object.keys(sidebarProjectCountsProp).length > 0) {
      return Object.values(sidebarProjectCountsProp).reduce((sum, d) => sum + (typeof d === 'object' && d != null ? d.count : 0), 0);
    }
    return serverSide ? (statusCountsFromApi.all?.count ?? totalCount) : bookings.length;
  }, [sidebarProjectCountsProp, serverSide, statusCountsFromApi.all?.count, totalCount, bookings.length]);

  const filtered = useMemo(() => {
    if (serverSide) {
      const q = search.toLowerCase().trim();
      if (!q) return bookings;
      return bookings.filter(b => {
        const proj = b.project?.name || '';
        return (
          b.booking_id?.toLowerCase().includes(q) ||
          b.lead?.name?.toLowerCase().includes(q) ||
          b.agent?.name?.toLowerCase().includes(q) ||
          proj.toLowerCase().includes(q)
        );
      });
    }
    const statusKey = activeStatusFilter === 'all' ? null : activeStatusFilter;
    return bookings.filter(b => {
      const matchStatus = !statusKey || (b.status || '').toLowerCase() === statusKey;
      const proj = b.project?.name || 'No Project';
      const matchProject = !activeProjectFilter || proj === activeProjectFilter || b.project?.id === activeProjectFilter;
      const q = search.toLowerCase().trim();
      const matchSearch = !q ||
        b.booking_id?.toLowerCase().includes(q) ||
        b.lead?.name?.toLowerCase().includes(q) ||
        b.agent?.name?.toLowerCase().includes(q) ||
        proj.toLowerCase().includes(q);
      return matchStatus && matchProject && matchSearch;
    });
  }, [bookings, serverSide, search, activeStatusFilter, activeProjectFilter]);

  const currentRPP = serverSide ? rowsPerPage : internalRowsPerPage;
  const clientTotalPages = Math.max(1, Math.ceil(filtered.length / currentRPP));
  const currentPage = serverSide ? page : internalPage;
  const paginated = serverSide ? filtered : filtered.slice((currentPage - 1) * currentRPP, currentPage * currentRPP);

  const totalDisplay = serverSide ? totalCount : filtered.length;
  const totalPagesDisplay = serverSide ? totalPages : clientTotalPages;

  return (
    <div style={{
      display:'flex', flexDirection:'column', height:'100%', fontFamily:"'DM Sans','Plus Jakarta Sans',sans-serif",
      background:T.bg, borderRadius:14, overflow:'hidden',
      border:`1.5px solid ${T.border}`,
      boxShadow:'0 2px 24px rgba(8,40,20,0.07)',
    }}>

      {/* Status filter is in parent BookingStatsBar; no inline header here */}

      <div style={{ display:'flex', flex:1, minHeight:0, overflowX:'hidden' }}>
      {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
      <div style={{
        width:220, flexShrink:0, background:T.sidebar,
        borderRight:`1.5px solid ${T.border}`,
        display:'flex', flexDirection:'column',
        overflowY:'auto',
      }}>
        {/* Sidebar header */}
        <div style={{ padding:'16px 16px 10px' }}>
          <div style={{ fontSize:10.5, fontWeight:800, color:T.muted, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:8 }}>
            Projects
          </div>
        </div>

        {/* All */}
        <SidebarProject
          project="All Bookings"
          count={allBookingsCount}
          active={!activeProjectFilter && activeStatusFilter === 'all'}
          onClick={() => {
            if (typeof onProjectFilterChange === 'function') onProjectFilterChange(null);
            if (typeof onStatusFilterChange === 'function') onStatusFilterChange('all');
          }}
        />

        {/* Per project */}
        {Object.entries(projectCounts).map(([name, data]) => {
          const count = typeof data === 'object' && data != null ? data.count : data;
          const projectId = typeof data === 'object' && data != null ? data.projectId : undefined;
          const isActive = activeProjectFilter != null && (activeProjectFilter === name || activeProjectFilter === projectId);
          return (
            <SidebarProject
              key={name}
              project={name}
              count={count}
              active={isActive}
              onClick={() => typeof onProjectFilterChange === 'function' ? onProjectFilterChange(projectId ?? name) : undefined}
            />
          );
        })}

        {/* Divider */}
        <div style={{ margin:'12px 14px 6px', borderTop:`1px solid ${T.border}` }} />
      </div>

      {/* ── MAIN PANEL ──────────────────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflowX:'hidden' }}>

        {/* Toolbar */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'12px 18px', background:T.white, borderBottom:`1.5px solid ${T.border}`,
          gap:12, flexWrap:'wrap',
        }}>
          {/* Left: Create Booking + title */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <button
              onClick={onCreateBooking}
              style={{
                display:'flex', alignItems:'center', gap:5,
                background:'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border:'none', borderRadius:8, padding:'7px 15px',
                fontSize:12.5, fontWeight:700, color:'#fff',
                cursor:'pointer', fontFamily:'inherit',
                boxShadow:'0 2px 10px rgba(10,107,62,0.22)',
                flexShrink:0,
              }}
            >
              <AddIcon sx={{ fontSize:15 }} /> Create Booking
            </button>
            <div>
              <span style={{ fontSize:14, fontWeight:800, color:T.ink, letterSpacing:'-0.3px' }}>
                {activeProjectFilter != null
                  ? (bookings.find(b => b.project?.id === activeProjectFilter || b.project?.name === activeProjectFilter)?.project?.name || 'Project')
                  : 'All Bookings'}
              </span>
              {activeStatusFilter && activeStatusFilter !== 'all' && (
                <span style={{ fontSize:12, color:T.muted, marginLeft:8 }}>
                  · {(statusCountsFromApi[activeStatusFilter]?.label || activeStatusFilter).replace(/\s*Approval\s*$/i, '').trim()}
                </span>
              )}
              <span style={{ fontSize:12, color:T.muted, marginLeft:6 }}>
                · {totalDisplay} booking{totalDisplay !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Table — only this area scrolls horizontally; vertical scroll for rows */}
        <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto' }}>
          {isLoading ? (
            <TableLoadingState message="Loading bookings…" theme={T} />
          ) : (
          <>
          <style>{TABLE_ROW_GLOBAL_CSS}</style>
          <table className="smart-table-wrap" style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, minWidth:900 }}>
            <thead>
              <tr>
                <SmartTableTH theme={T} width={140}>Booking</SmartTableTH>
                <SmartTableTH theme={T} width={110}>Booking Date</SmartTableTH>
                <SmartTableTH theme={T} width={110}>Booking Status</SmartTableTH>
                <SmartTableTH theme={T} width={150}>Lead</SmartTableTH>
                <SmartTableTH theme={T} width={165}>Unit · Project</SmartTableTH>
                <SmartTableTH theme={T} width={95} align="right">Unit Price</SmartTableTH>
                <SmartTableTH theme={T} width={100} align="right">Booking Amount</SmartTableTH>
                <SmartTableTH theme={T} width={95} align="right">Token Amount</SmartTableTH>
                <SmartTableTH theme={T} width={120}>Agent</SmartTableTH>
                <SmartTableTH theme={T} width={72} align="center">Action</SmartTableTH>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <TableEmptyState
                  message="No bookings found"
                  description="Try adjusting your filters or search query."
                  theme={T}
                  colSpan={10}
                />
              ) : paginated.map((b) => {
                const cfg = STATUS_CFG(b.status);
                const hasAmt = b.booking_amount > 0;
                return (
                  <tr
                    key={b.id}
                    className={TABLE_ROW_CLASS}
                  >
                    {/* Booking */}
                    <SmartTableTD theme={T}>
                      <a
                        onClick={(e)=>{ e.preventDefault(); navigate(`/bookings/${b.id}`); }}
                        href={`/bookings/${b.id}`}
                        style={{ textDecoration:'none', display:'block' }}
                      >
                        <div style={{ fontSize:12.5, fontWeight:700, color:T.green, letterSpacing:'-0.2px' }}>{b.booking_id}</div>
                      </a>
                      <div style={{ fontSize:10.5, color:T.muted, marginTop:1 }}>{b.created_at}</div>
                    </SmartTableTD>

                    {/* Booking Date */}
                    <SmartTableTD theme={T} style={{ whiteSpace:'nowrap' }}>
                      <div style={{ fontSize:12.5, color: b.booking_date ? T.ink2 : T.muted, fontWeight: b.booking_date ? 500 : 400 }}>
                        {fmtDate(b.booking_date)}
                      </div>
                      {b.booking_date && (
                        <div style={{ fontSize:10.5, color:T.muted, marginTop:1 }}>{daysSince(b.booking_date)}</div>
                      )}
                    </SmartTableTD>

                    {/* Booking Status */}
                    <SmartTableTD theme={T}><StatusPill status={b.status} label={b.status_verbose} /></SmartTableTD>

                    {/* Lead */}
                    <SmartTableTD theme={T}>
                      {b.lead
                        ? <>
                            <div style={{ fontSize:10.5, color:T.muted, marginBottom:1 }}>#{b.lead.lead_id}</div>
                            <a
                              onClick={(e)=>{ e.preventDefault(); navigate(`/leads/${b.lead.id}`); }}
                              href={`/leads/${b.lead.id}`}
                              style={{ fontSize:12.5, fontWeight:600, color:T.green, textDecoration:'none' }}
                            >{b.lead.name}</a>
                          </>
                        : <span style={{ color:T.muted }}>—</span>
                      }
                    </SmartTableTD>

                    {/* Unit · Project */}
                    <SmartTableTD theme={T}>
                      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                        {b.unit && b.project?.id && b.unit.configuration_id ? (
                          <a
                            onClick={(e)=>{ e.preventDefault(); navigate(`/inventory/projects/${b.project.id}/configurations/${b.unit.configuration_id}/units/${b.unit.id}`); }}
                            href={`/inventory/projects/${b.project.id}/configurations/${b.unit.configuration_id}/units/${b.unit.id}`}
                            style={{ display:'inline-flex', alignItems:'center', gap:4, textDecoration:'none', color:'inherit' }}
                          >
                            <span style={{ fontSize:11, color:T.muted }}>🏠</span>
                            <span style={{ fontSize:12.5, fontWeight:600, color:T.ink2 }}>{b.unit.name || b.unit.unit_code || '—'}</span>
                          </a>
                        ) : b.unit ? (
                          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <span style={{ fontSize:11, color:T.muted }}>🏠</span>
                            <span style={{ fontSize:12.5, fontWeight:600, color:T.ink2 }}>{b.unit.name || b.unit.unit_code || '—'}</span>
                          </div>
                        ) : (
                          <span style={{ fontSize:11.5, color:T.muted, fontStyle:'italic' }}>No unit</span>
                        )}
                        {b.project
                          ? <a
                              onClick={(e)=>{ e.preventDefault(); navigate(`/inventory/projects/${b.project.id}`); }}
                              href={`/inventory/projects/${b.project.id}`}
                              style={{ display:'inline-flex', alignItems:'center', gap:4, textDecoration:'none' }}
                            >
                              <span style={{ fontSize:11, color:T.green }}>🏢</span>
                              <span style={{ fontSize:12, fontWeight:600, color:T.green }}>{b.project.name}</span>
                            </a>
                          : <span style={{ fontSize:11, color:T.muted, fontStyle:'italic' }}>No project</span>
                        }
                      </div>
                    </SmartTableTD>

                    {/* Sales Price */}
                    <SmartTableTD theme={T} align="right">
                      <span style={{ fontSize:13, fontWeight:700, color: hasAmt ? T.green : T.muted }}>
                        {hasAmt ? (b.unit?.base_price_str || '—') : '—'}
                      </span>
                    </SmartTableTD>

                    {/* Booking Amount */}
                    <SmartTableTD theme={T} align="right">
                      <span style={{ fontSize:12.5, fontWeight:600, color: hasAmt ? T.ink2 : T.muted }}>
                        {b.booking_amount_str || '—'}
                      </span>
                    </SmartTableTD>

                    {/* Token Amount */}
                    <SmartTableTD theme={T} align="right">
                      <span style={{ fontSize:12.5, fontWeight:600, color: hasAmt ? T.ink2 : T.muted }}>
                        {b.token_amount_str || '—'}
                      </span>
                    </SmartTableTD>

                    {/* Agent */}
                    <SmartTableTD theme={T}><AgentAvatar name={b.agent?.name} /></SmartTableTD>

                    {/* Action */}
                    <SmartTableTD theme={T} align="center" style={{ paddingRight:14 }}>
                      <BookingAction booking={b} onStatusUpdate={onStatusUpdate} />
                    </SmartTableTD>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
          )}
        </div>

        {/* Pagination (only when not controlled by parent) */}
        {!hideTablePagination && (totalDisplay > 0) && (
          <div style={{ borderTop:`1px solid ${T.border}`, background:'#f8fbf9', padding:'0 8px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px' }}>
              <span style={{ fontSize:12, color:T.muted }}>
                Showing {Math.min((currentPage - 1) * currentRPP + 1, totalDisplay)}–{Math.min(currentPage * currentRPP, totalDisplay)} of {totalDisplay}
              </span>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:12, color:T.muted }}>Rows:</span>
                {[10, 15, 25, 50].map(n => (
                  <button
                    key={n}
                    onClick={() => {
                      if (serverSide && typeof onRowsPerPageChange === 'function') onRowsPerPageChange(n);
                      else { setInternalRowsPerPage(n); setInternalPage(1); }
                    }}
                    style={{
                      border: `1px solid ${currentRPP === n ? T.green : T.border}`,
                      borderRadius: 6, padding: '3px 9px', fontSize: 11.5, fontWeight: currentRPP === n ? 700 : 500,
                      color: currentRPP === n ? T.green : T.subtle,
                      background: currentRPP === n ? T.greenLt : 'transparent',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >{n}</button>
                ))}
                <div style={{ width: 1, height: 18, background: T.border, margin: '0 4px' }} />
                <button
                  onClick={() => { if (serverSide && typeof onPageChange === 'function') onPageChange(currentPage - 1); else setInternalPage(p => Math.max(1, p - 1)); }}
                  disabled={currentPage <= 1}
                  style={{
                    border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 10px',
                    fontSize: 12, color: currentPage <= 1 ? T.muted : T.subtle,
                    background: 'transparent', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}
                >‹</button>
                <span style={{ fontSize: 12, color: T.subtle, fontWeight: 600 }}>
                  {currentPage} / {totalPagesDisplay}
                </span>
                <button
                  onClick={() => { if (serverSide && typeof onPageChange === 'function') onPageChange(currentPage + 1); else setInternalPage(p => Math.min(totalPagesDisplay, p + 1)); }}
                  disabled={currentPage >= totalPagesDisplay}
                  style={{
                    border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 10px',
                    fontSize: 12, color: currentPage >= totalPagesDisplay ? T.muted : T.subtle,
                    background: 'transparent', cursor: currentPage >= totalPagesDisplay ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}
                >›</button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default BookingsListView;
